import React, { useEffect, useRef, useCallback, memo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import DeleteBtn from './DeleteBtn';
import { updateCrop, setCropList, deleteCrop } from '@modules/editor';
import { toast, Flip } from 'react-toastify';

const CropRow = memo(() => {
    const dispatch = useDispatch();
    const xRef = useRef(null);
    const yRef = useRef(null);
    const widthRef = useRef(null);
    const heightRef = useRef(null);
    const cropList = useSelector((state) => state.editor.cropList);
    const canvasSize = useSelector((state) => state.editor.canvasSize);
    const el = cropList;
    const isEditing = el?.isEditing || false;
    const [draft, setDraft] = useState({
        x: el?.x?.toString() || "100",
        y: el?.y?.toString() || "100",
        width: el?.width?.toString() || "200",
        height: el?.height?.toString() || "200"
    });
    const [errorFields, setErrorFields] = useState({});

    const isValidNumberString = (value: string) => /^[0-9]+$/.test(value) && Number(value) > 0;

    // 유효성 검사 함수들
    const validateX = (x: number, width: number) => {
        return x >= 0 && x + width <= canvasSize.canvasWidth;
    };

    const validateY = (y: number, height: number) => {
        return y >= 0 && y + height <= canvasSize.canvasHeight;
    };

    const validateWidth = (x: number, width: number) => {
        return width > 0 && x + width <= canvasSize.canvasWidth;
    };

    const validateHeight = (y: number, height: number) => {
        return height > 0 && y + height <= canvasSize.canvasHeight;
    };

    // 숫자 형식 검사만 하는 함수 (UI 표시용)
    const getNumberFormatErrors = (fields: any) => ({
        x: !isValidNumberString(fields.x),
        y: !isValidNumberString(fields.y),
        width: !isValidNumberString(fields.width),
        height: !isValidNumberString(fields.height)
    });

    // 전체 유효성 검사 함수 (저장 시 검사용)
    const getValidationErrors = (fields: any) => ({
        x: !isValidNumberString(fields.x) || !validateX(Number(fields.x), Number(fields.width)),
        y: !isValidNumberString(fields.y) || !validateY(Number(fields.y), Number(fields.height)),
        width: !isValidNumberString(fields.width) || !validateWidth(Number(fields.x), Number(fields.width)),
        height: !isValidNumberString(fields.height) || !validateHeight(Number(fields.y), Number(fields.height))
    });

    useEffect(() => {
        if (isEditing && el) {
            setDraft({
                x: el.x?.toString() || "100",
                y: el.y?.toString() || "100",
                width: el.width?.toString() || "200",
                height: el.height?.toString() || "200"
            });
            if (xRef.current) xRef.current.textContent = el.x.toString();
            if (yRef.current) yRef.current.textContent = el.y.toString();
            if (widthRef.current) widthRef.current.textContent = el.width.toString();
            if (heightRef.current) heightRef.current.textContent = el.height.toString();
        }
    }, [isEditing, el]);

    const toggleEdit = () => {
        if (cropList.isEditing) {
            const errors = getValidationErrors(draft);
            if (Object.values(errors).some(Boolean)) {
                let errorMessage = "값을 올바르게 입력하세요";

                if (errors.x) {
                    errorMessage = `X 좌표는 0 이상 ${Math.floor(canvasSize.canvasWidth - Number(draft.width))} 이하의 정수여야 합니다.`;
                } else if (errors.y) {
                    errorMessage = `Y 좌표는 0 이상 ${Math.floor(canvasSize.canvasHeight - Number(draft.height))} 이하의 정수여야 합니다.`;
                } else if (errors.width) {
                    errorMessage = `가로 크기는 1 이상 ${Math.floor(canvasSize.canvasWidth - Number(draft.x))} 이하의 정수여야 합니다.`;
                } else if (errors.height) {
                    errorMessage = `세로 크기는 1 이상 ${Math.floor(canvasSize.canvasHeight - Number(draft.y))} 이하의 정수여야 합니다.`;
                }

                toast.error(errorMessage, {
                    position: "top-center",
                    transition: Flip
                });
                return;
            }
            dispatch(setCropList({
                ...cropList,
                x: Number(draft.x),
                y: Number(draft.y),
                width: Number(draft.width),
                height: Number(draft.height),
                isEditing: false
            }));
            return;
        }
        dispatch(setCropList({ ...cropList, isEditing: true }));
    };

    if (!el) return null;

    return (
        <Box className="crop-row">
            <Box className="crop-label">
                <Typography>크롭</Typography>
            </Box>
            <Box className="crop-position-size">
                <Box className="crop-position">
                    <Typography>위치</Typography>
                    <Box className="crop-coordinates">
                        <Box className="crop-coordinate">
                            <Typography>X</Typography>
                            {isEditing ? (
                                <div
                                    ref={xRef}
                                    className="crop editable-text is-editable"
                                    contentEditable
                                    suppressContentEditableWarning
                                    style={{ color: errorFields.x ? 'red' : undefined }}
                                    onInput={(e) => {
                                        const value = e?.currentTarget?.textContent ?? "";
                                        setDraft(prev => ({ ...prev, x: value }));
                                        setErrorFields(prev => ({
                                            ...prev,
                                            x: !isValidNumberString(value)
                                        }));
                                    }}
                                />
                            ) : (
                                <div className="crop">{el.x}</div>
                            )}
                        </Box>
                        <Box className="crop-coordinate">
                            <Typography>Y</Typography>
                            {isEditing ? (
                                <div
                                    ref={yRef}
                                    className="crop editable-text is-editable"
                                    contentEditable
                                    suppressContentEditableWarning
                                    style={{ color: errorFields.y ? 'red' : undefined }}
                                    onInput={(e) => {
                                        const value = e?.currentTarget?.textContent ?? "";
                                        setDraft(prev => ({ ...prev, y: value }));
                                        setErrorFields(prev => ({
                                            ...prev,
                                            y: !isValidNumberString(value)
                                        }));
                                    }}
                                />
                            ) : (
                                <div className="crop">{el.y}</div>
                            )}
                        </Box>
                    </Box>
                </Box>
                <Box className="crop-size">
                    <Typography>크기</Typography>
                    <Box className="crop-dimensions">
                        <Box className="crop-dimension">
                            <Typography>가로</Typography>
                            {isEditing ? (
                                <div
                                    ref={widthRef}
                                    className="crop editable-text is-editable"
                                    contentEditable
                                    suppressContentEditableWarning
                                    style={{ color: errorFields.width ? 'red' : undefined }}
                                    onInput={(e) => {
                                        const value = e?.currentTarget?.textContent ?? "";
                                        setDraft(prev => ({ ...prev, width: value }));
                                        setErrorFields(prev => ({
                                            ...prev,
                                            width: !isValidNumberString(value)
                                        }));
                                    }}
                                />
                            ) : (
                                <div className="crop">{el.width}</div>
                            )}
                        </Box>
                        <Box className="crop-dimension">
                            <Typography>세로</Typography>
                            {isEditing ? (
                                <div
                                    ref={heightRef}
                                    className="crop editable-text is-editable"
                                    contentEditable
                                    suppressContentEditableWarning
                                    style={{ color: errorFields.height ? 'red' : undefined }}
                                    onInput={(e) => {
                                        const value = e?.currentTarget?.textContent ?? "";
                                        setDraft(prev => ({ ...prev, height: value }));
                                        setErrorFields(prev => ({
                                            ...prev,
                                            height: !isValidNumberString(value)
                                        }));
                                    }}
                                />
                            ) : (
                                <div className="crop">{el.height}</div>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box className="crop-actions">
                <Box className="crop-edit-button" onClick={toggleEdit}>
                    {isEditing ? <CheckIcon /> : <EditIcon />}
                </Box>
                <DeleteBtn />
            </Box>
        </Box>
    );
});

export default CropRow; 